<template>
  <div class="main-content">
    <div class="section__content section__content--p30">
      <div class="container-fluid job-info-container">
        <v-card class="p-b-40 p-t-30 p-l-30 p-r-30">
          <div class="el-loading-mask" style="bottom: 0" v-if="loading">
            <div class="el-loading-spinner">
              <svg viewBox="25 25 50 50" class="circular">
                <circle cx="50" cy="50" r="20" fill="none" class="path"></circle>
              </svg>
            </div>
          </div>
          <!-- Job Name -->
          <div class="row">
            <div class="col-md-12 header">
              <h3 class="title-7 p-b-10">
                <v-btn
                  text icon
                  class="m-0"
                  @click="$router.back()">
                  <i class="zmdi zmdi-long-arrow-left"></i>
                </v-btn>
                {{ job.name }}
              </h3>
              <span class="p-l-20 status--gray">ID={{ job.id }}</span>
            </div>
            <!--div class="col-md-2 text-right">
              <a href="#" @click.prevent="goToNextJob()" v-if="nextJob !== 0">Next job</a>
            </div-->
          </div>
          <v-divider></v-divider>
          <!-- Status and general Info -->
          <div class="row">
            <div class="col-md-2">
              <div class="info-title">
                {{ text('job_status') }}
              </div>
              <i class="fa fa-clock job-status--paused"
                 v-if="jobStatus === 'pending'">
              </i>
              <i class="fa fa-circle-notch fa-spin job-status--finished"
                 v-if="jobStatus === 'processing'">
              </i>
              <i class="fa fa-check-circle job-status--finished"
                 v-if="jobStatus === 'done'">
              </i>
              <i class="fa fa-exclamation-circle job-status--failed"
                 v-if="jobStatus === 'input_failed' || jobStatus === 'output_failed'">
              </i>
              <span class="p-l-10">{{ text(jobStatus) }}</span>
            </div>
            <div class="col-md-3">
              <div class="info-title">
                {{ text('profile') }}
              </div>
              <div class="p-b-5" v-if="!job.profile.name">
                {{ job.profile.name || 'N/A' }}
              </div>
              <v-btn
                v-else
                small
                outline
                class="m-0"
                @click="profileInfoDialog=true">
                {{ job.profile.name | truncate(25) }}
              </v-btn>
            </div>
            <div class="col-md-1">
              <div>
                <a href="https://hls-js.netlify.app/demo/" target="_blank">
                  <i class="fa fa-play-circle p-r-5"></i> {{ text('stream_tester') }}
                </a>
              </div>
            </div>
            <div class="col-md-2">
              <div class="info-title">
                <i class="zmdi zmdi-calendar p-r-10"></i>{{ text('submitted') }}
              </div>
              <div>
                {{ formatTime(job.create_time) }}
              </div>
            </div>
            <div class="col-md-2">
              <div class="info-title">
                <i class="zmdi zmdi-calendar p-r-10"></i>{{ text('finished') }}
              </div>
              <div>
                <span v-if="job.state === 'done'">
                  {{ formatTime(job.update_time) }}
                </span>
                <span v-else>
                  <i class="zmdi zmdi-more"></i>
                </span>
              </div>
            </div>
            <div class="col-md-2">
              <div class="info-title">
                <i class="zmdi zmdi-time p-r-5"></i>
                {{ text('total_job_time') }}
              </div>
              <div>
                <span v-if="job.state === 'done'">
                  {{ jobTime }}
                </span>
                <span v-else>
                  {{ jobTimeCalc }}
                </span>
              </div>
            </div>
          </div>
          <v-divider></v-divider>
          <!-- Input & Output -->
          <div class="row">
            <div class="col-md-12">
              <!-- Input -->
              <table class="job-output m-t-50">
                <tbody>
                  <tr>
                    <td class="column-title">
                      {{ text('input') }}
                      </td>
                    <td colspan="2">
                      <i class="fa fa-clock job-status--paused"
                         v-if="inputStatus === 'pending'">
                      </i>
                      <i class="fa fa-circle-notch fa-spin job-status--finished"
                         v-if="inputStatus === 'processing'">
                      </i>
                      <i class="fa fa-check-circle job-status--finished"
                         v-if="inputStatus === 'finished'">
                      </i>
                      <i class="fa fa-exclamation-circle job-status--failed"
                         v-if="inputStatus === 'failed'">
                      </i>
                      <span class="p-l-10">{{ text(inputStatus) }}</span>
                    </td>
                  </tr>
                  <tr v-if="job.input.error">
                    <td class="column-error">
                      {{ text('error') }}
                    </td>
                    <td colspan="2" class="column-error">
                      {{ job.input.error }}
                    </td>
                  </tr>
                  <tr>
                    <td>URL</td>
                    <td class="link" colspan="2">
                      <a :href="job.input.url" target="_blank">
                        {{ job.input.url }}
                      </a>
                      <i @click="handleCopy(job.input.url)"
                         class="zmdi zmdi-copy m-l-10">
                      </i>
                    </td>
                  </tr>
                  <tr>
                    <td>{{ text('file_size') }}</td>
                    <td colspan="2">
                      <span v-if="inputFileSize">{{ inputFileSize }}</span>
                    </td>
                  </tr>
                  <tr>
                    <td>{{ text('video_duration') }}</td>
                    <td colspan="2">
                      {{ inputDuration }}
                    </td>
                  </tr>
                  <tr>
                    <td>{{ text('resolution') }}</td>
                    <td>
                      <span :key="resolution+resInd" v-for="(resolution, resInd) in inputResolution">
                        {{ resolution }}
                        <span class="p-l-5 p-r-5" v-if="resInd < inputResolution.length-1">&#183;</span>
                      </span>
                    </td>
                  </tr>
                  <tr>
                    <td>{{ text('format') }}</td>
                    <td colspan="2">
                      <span v-if="inputMetadata && inputMetadata.format">
                        {{ inputMetadata.format.format_long_name }}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
              <!-- Outputs -->
              <table class="job-output m-t-50" :key="output.id" v-for="(output, i) of job.outputs">
                <tbody>
                <tr>
                  <td class="column-title">
                    Output #{{ i+1 }}
                  </td>
                  <td colspan="2">
                    <i class="fa fa-clock job-status--paused"
                       v-if="outputStatus(output) === 'pending'">
                    </i>
                    <i class="fa fa-circle-notch fa-spin job-status--finished"
                       v-if="outputStatus(output) === 'processing'">
                    </i>
                    <i class="fa fa-check-circle job-status--finished"
                       v-if="outputStatus(output) === 'finished'">
                    </i>
                    <i class="fa fa-exclamation-circle job-status--failed"
                       v-if="outputStatus(output) === 'failed'">
                    </i>
                    <span class="p-l-10">{{ text(outputStatus(output)) }}</span>
                    <span v-if="outputStatus(output) === 'Processing'">
                        {{ output.progress }}%
                    </span>
                  </td>
                </tr>
                <tr v-if="output.error">
                  <td class="column-error">
                    {{ text('error') }}
                  </td>
                  <td colspan="2" class="column-error">
                    {{ output.error }}
                  </td>
                </tr>
                <tr :key="url" v-for="(url, urlIndex) of output.url">
                  <td>
                    <i class="zmdi zmdi-view-subtitles p-r-10 pointer"
                       v-if="areThereOutputSubtitles(i)"
                       @click="toggleSubtitles(i)"></i>
                    <i class="zmdi zmdi-archive p-r-10" v-if="output.zip"></i>
                    URL <span v-if="output.url.length>1">#{{ urlIndex+1 }}</span></td>
                  <td class="link" colspan="2">
                    <a :href="outputURL(url)" target="_blank">{{ outputURL(url) }}</a>
                    <i @click="handleCopy(outputURL(url))" class="zmdi zmdi-copy m-l-10"></i>
                  </td>
                </tr>
                <tr v-if="output.showSubtitles">
                  <td>Subtitles source</td>
                  <td colspan="2">
                    <template v-for="(sub, subIndex) of outputSubtitles(i)">
                      <a :key="sub+subIndex" :href="sub" target="_blank">{{ sub }}</a>
                    </template>
                  </td>
                </tr>
                <tr>
                  <td>{{ text('file_size') }}</td>
                  <td colspan="2">
                    {{ outputFileSize(i) }}
                  </td>
                </tr>
                <tr>
                  <td>{{ text('video_duration') }}</td>
                  <td colspan="2">
                    {{ outputDuration(i) }}
                  </td>
                </tr>
                <tr>
                  <td>{{ text('resolution') }}</td>
                  <td>
                    <span :key="resolution+resInd" v-for="(resolution, resInd) in outputResolutions(i)">
                      {{ resolution }}
                      <span class="p-l-5 p-r-5" v-if="resInd < outputResolutions(i).length-1">&#183;</span>
                    </span>
                  </td>
                </tr>
                <tr>
                  <td>{{ text('format') }}</td>
                  <td colspan="2">
                    {{ formats[output.format] }}
                    <span v-if="output.format==='segmented_hls'">v{{ output.hls_version || 3 }}</span>
                    <span class="p-l-10" v-if="output.segment_size">
                      {{ text('segment_duration') }}: {{ output.segment_size }} {{ text('sec') }}
                    </span>
                  </td>
                </tr>
                <tr v-if="output.drm">
                  <td>
                    <i class="zmdi zmdi-lock p-r-10" v-if="output.drm"></i>
                    DRM
                  </td>
                  <td>
                    {{ drmMap[output.drm] }}
                  </td>
                  <td>
                    <v-btn class="m-0 float-right" outline small @click="toggleDRMInfo(i)">
                      {{ output.drmInfo ? 'Hide' : 'Show'}} details
                    </v-btn>
                  </td>
                </tr>
                <tr class="drm-row" v-show="output.drm==='widevine' && output.drmInfo">
                  <td>Key Server URL</td>
                  <td colspan="2">
                    <a :href="output.widevine_license_server" target="_blank">
                      {{ output.widevine_license_server }}
                    </a>
                  </td>
                </tr>
                <tr class="drm-row" v-if="output.drm==='widevine' && output.drmInfo">
                  <td>Provider</td>
                  <td colspan="2">
                    {{ output.widevine_provider }}
                  </td>
                </tr>
                <tr class="drm-row" v-if="output.drm==='widevine' && output.drmInfo">
                  <td>Key</td>
                  <td colspan="2">
                    {{ output.widevine_key }}
                  </td>
                </tr>
                <tr class="drm-row" v-if="output.drm==='widevine' && output.drmInfo">
                  <td>IV</td>
                  <td colspan="2">
                    {{ output.widevine_iv }}
                  </td>
                </tr>
                <tr class="drm-row" v-if="output.drm==='widevine' && output.drmInfo">
                  <td>Package Name</td>
                  <td colspan="2">
                    {{ output.widevine_package_name }}
                  </td>
                </tr>
                <tr class="drm-row" v-if="output.drm==='playready' && output.drmInfo">
                  <td>Key Server URL</td>
                  <td colspan="2">
                    <a :href="output.playready_la_url" target="_blank">
                      {{ output.playready_la_url }}
                    </a>
                  </td>
                </tr>
                <tr class="drm-row" v-if="output.drm==='playready' && output.drmInfo">
                  <td>Key ID</td>
                  <td colspan="2">
                    {{ output.playready_key_id }}
                  </td>
                </tr>
                <tr class="drm-row" v-if="output.drm==='playready' && output.drmInfo">
                  <td>KeySeed</td>
                  <td colspan="2">
                    {{ output.playready_key_seed }}
                  </td>
                </tr>
                <tr class="drm-row" v-if="output.drm==='playready' && output.drmInfo">
                  <td>Content Key</td>
                  <td colspan="2">
                    {{ output.playready_content_key }}
                  </td>
                </tr>
                </tbody>
              </table>
            </div>
          </div>
        </v-card>

        <!-- Profile Info Dialog -->
        <v-dialog v-model="profileInfoDialog" max-width="900">
          <v-card class="p-b-15" v-if="!loading">
            <v-card-text>
              <div class="row">
                <div class="col-md-12">
                  <v-btn icon @click="profileInfoDialog = false">
                    <v-icon>zmdi-close</v-icon>
                  </v-btn>
                  <span class="headline align-middle"> {{ job.profile.name }}</span>
                </div>
              </div>
              <v-divider/>
              <table class="profile-detail-table">
                <tbody>
                <tr>
                  <td><span class="card-item-title">ID</span></td>
                  <td>{{ job.profile.id }}</td>
                </tr>
                <tr>
                  <td>
                    <span class="card-item-title">
                      {{ text('audio_codec') }}
                    </span>
                  </td>
                  <td class="text-uppercase">{{ job.profile.audio_template.codec }}</td>
                </tr>
                <tr>
                  <td><span class="card-item-title">{{ text('video_codec') }}</span></td>
                  <td>
                    <span class="text-uppercase" v-if="job.profile.video_template.codec">
                      {{ videoCodecMap[job.profile.video_template.codec][0] }}
                    </span>
                  </td>
                </tr>
                <tr>
                  <td>
                    <span class="card-item-title">
                      {{ text('video_streams') }}
                    </span>
                  </td>
                  <td>{{ job.profile.video_template.streams.length }}</td>
                </tr>
                <tr v-if="job.profile.video_template.codec.startsWith('h26')">
                  <td><span class="card-item-title">{{ text('encoder') }}</span></td>
                  <td>
                    <span v-if="job.profile.video_template.codec">
                      {{ videoCodecMap[job.profile.video_template.codec][1]
                      || videoCodecMap[job.profile.video_template.codec][2] }}
                    </span>
                  </td>
                </tr>
                </tbody>
              </table>
              <v-divider/>
              <table class="profile-detail-table">
                <tbody>
                <tr>
                  <td><span class="card-item-title">{{ text('audio_channels') }}</span></td>
                  <td>
                    {{ audioChannelsMap[job.profile.audio_template.channels] || text('same_as_source') }}
                  </td>
                </tr>
                <tr>
                  <td><span class="card-item-title">{{ text('audio_bitrate') }}</span></td>
                  <td>{{ job.profile.audio_template.bitrate }} Kbps</td>
                </tr>
                <tr>
                  <td><span class="card-item-title">{{ text('sample_rate') }}</span></td>
                  <td>
                    <span v-if="job.profile.audio_template.sample_rate">
                      {{ job.profile.audio_template.sample_rate }} Hz
                    </span>
                    <span v-else>{{ text('same_as_source') }}</span>
                  </td>
                </tr>
                </tbody>
              </table>
              <template v-if="job.profile.video_template.streams.length">
                <table class="profile-streams m-t-15">
                  <thead>
                  <tr>
                    <th class="number-column">{{ text('video_stream') }}</th>
                    <th v-if="job.profile.video_template.codec.startsWith('h264')">
                      Profile
                    </th>
                    <th v-if="job.profile.video_template.codec.startsWith('h265')">
                      Profile
                    </th>
                    <th v-if="job.profile.video_template.codec.startsWith('h26')">
                      Level
                    </th>
                    <th>{{ text('resolution') }}</th>
                    <th>{{ text('bitrate_kbps') }}</th>
                    <th>{{ text('resize_mode') }}</th>
                    <th>{{ text('frame_rate') }}</th>
                    <th>KeyInt / MinKeyInt</th>
                    <th v-if="job.profile.video_template.codec.startsWith('h26')">
                      Ref Frames / BFrames
                    </th>
                  </tr>
                  </thead>
                  <tbody>
                  <tr v-for="(stream, i) of job.profile.video_template.streams" :key="i">
                    <td>#{{ i+1 }}</td>
                    <td v-if="job.profile.video_template.codec.startsWith('h264')">
                      {{ h264ProfileMap[stream.h264_codec_profile] }}
                    </td>
                    <td v-if="job.profile.video_template.codec.startsWith('h265')">
                      {{ h265ProfileMap[stream.h264_codec_profile] }}
                    </td>
                    <td v-if="job.profile.video_template.codec.startsWith('h264')">
                      {{ stream.h264_codec_level || 'Auto' }}
                    </td>
                    <td v-if="job.profile.video_template.codec.startsWith('h265')">
                      {{ stream.h264_codec_level || 'Auto' }}
                    </td>
                    <td>{{ stream.resolution || 'Same as source'}}</td>
                    <td>{{ stream.bitrate }}</td>
                    <td>{{ resizeModeMap[stream.aspect_ratio] || 'Same as source' }}</td>
                    <td>
                      <span v-if="stream.target_fps">{{ stream.target_fps }} fps</span>
                      <span v-else>Same as source</span>
                    <td>{{ stream.keyframe_int || '0' }}/{{ stream.min_keyframe_int || '0' }}</td>
                    <td v-if="job.profile.video_template.codec.startsWith('h26')">
                      {{ stream.h264_reference_frames || '0' }}/{{ stream.h264_bframes || '0' }}
                    </td>
                  </tr>
                  </tbody>
                </table>
              </template>
            </v-card-text>
          </v-card>
        </v-dialog>
        <!-- -->

        <!-- Logs -->
        <v-card class="m-t-20 m-b-30 p-b-30 p-t-30 p-l-30 p-r-30">
          <div class="row">
            <div class="col-md-10 header">
              <h3 class="title-7 p-b-10">
                {{ text('logs') }}
              </h3>
            </div>
            <div class="col-md-2 text-right">
              {{ events.length }} {{ text('events_count') }}
            </div>
          </div>
          <div class="row" v-if="events.length">
            <div class="col-md-12">
              <v-timeline dense clipped>
                <v-timeline-item
                  class="mb-0"
                  :color="logColor(events[0])"
                  small>
                  <v-layout justify-space-between>
                    <v-flex xs7>{{ eventMap[events[0].name].message(events[0]) }}</v-flex>
                    <v-flex xs5 text-xs-right>{{ formatLogDate(events[0].timestamp) }}</v-flex>
                  </v-layout>
                </v-timeline-item>
                <template v-if="events.length > 1">
                  <v-timeline-item
                    class="mb-0"
                    hide-dot>
                    <v-btn
                      outline small
                      class="mb-0"
                      @click="displayLogs = !displayLogs">
                      {{ text('show_btn') }} {{ displayLogs ? text('less') : text('more') }}
                    </v-btn>
                  </v-timeline-item>
                  <template v-if="displayLogs">
                    <v-timeline-item
                      v-for="(event, eventIndex) in events.slice(1)"
                      :key="event.timestamp+eventIndex"
                      class="mb-0"
                      :color="logColor(event)"
                      small>
                      <v-layout justify-space-between>
                        <v-flex xs7>{{ eventMap[event.name].message(event) }}</v-flex>
                        <v-flex xs5 text-xs-right>{{ formatLogDate(event.timestamp) }}</v-flex>
                      </v-layout>
                    </v-timeline-item>
                  </template>
                </template>
              </v-timeline>
            </div>
          </div>
        </v-card>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import moment from 'moment';
import { setClipboard, formatBytes } from '@/helpers/utils';
import {
  INPUT_STATES, OUTPUT_STATES, JOB_TYPES, JOB_EVENTS, VIDEO_CODEC_MAP, AUDIO_CHANNELS_MAP,
  H264_CODEC_PROFILE_MAP, H265_CODEC_PROFILE_MAP, RESIZE_MODE_MAP, DRM,
} from '@/components/transcoding/shared/types';

function secondsToDuration(secs) {
  const hours = Math.floor(moment.duration(secs, 'seconds').asHours());
  const minutes = moment.duration(secs, 'seconds').minutes();
  const seconds = moment.duration(secs, 'seconds').seconds();
  return hours > 0 ? `${hours}h ${minutes}m ${seconds}s` : `${minutes}m ${seconds}s`;
}

export default {
  created() {
    const jobId = this.$route.params.id;
    this.getJobById(jobId, true);
    this.pollData(jobId);
    this.currentT = moment().unix();
    this.updateTimer();
  },
  beforeDestroy() {
    clearInterval(this.polling);
  },
  data() {
    return {
      profileInfoDialog: false,
      inputStates: INPUT_STATES,
      outputStates: OUTPUT_STATES,
      formats: JOB_TYPES,
      eventMap: JOB_EVENTS,
      job: {
        name: '',
        input: {
          url: '',
        },
        profile: {
          audio_template: { name: '' },
          video_template: { name: '' },
        },
        outputs: [],
      },
      inputMetadata: {},
      outputsMetadata: [],
      logsLoading: false,
      events: [],
      displayLogs: false,
      videoCodecMap: VIDEO_CODEC_MAP,
      audioChannelsMap: AUDIO_CHANNELS_MAP,
      h264ProfileMap: H264_CODEC_PROFILE_MAP,
      h265ProfileMap: H265_CODEC_PROFILE_MAP,
      resizeModeMap: RESIZE_MODE_MAP,
      drmMap: DRM,
      currentT: '',
      jobTimeCalc: '',
      polling: null,
    };
  },
  computed: {
    loading() {
      return this.$store.getters.isDataLoaded;
    },
    jobStatus() {
      if (this.job.input.state === 'pending') {
        return 'pending';
      }
      if (this.job.input.state === 'processing') {
        return 'processing';
      }
      if (this.job.input.state === 'error') {
        return 'input_failed';
      }
      if (this.job.outputs.length) {
        if (this.job.outputs[0].state === 'pending') {
          return 'processing';
        }
        if (this.job.outputs[0].state === 'error') {
          return 'output_failed';
        }
        if (this.job.outputs[0].state === 'success') {
          return 'done';
        }
      }
      return 'processing';
    },
    inputStatus() {
      if (this.job.input.state === 'pending') {
        return 'pending';
      }
      if (this.job.input.state === 'processing') {
        return 'processing';
      }
      if (this.job.input.state === 'error') {
        return 'failed';
      }
      return 'finished';
    },
    jobTime() {
      if (this.job.state !== 'done') return '';
      const diff = moment(this.job.update_time).diff(moment(this.job.create_time), 'seconds');
      return secondsToDuration(diff);
    },
    inputFileSize() {
      if (this.inputMetadata && this.inputMetadata.format) {
        return formatBytes(this.inputMetadata.format.size);
      }
      return '';
    },
    inputDuration() {
      if (this.inputMetadata && this.inputMetadata.format) {
        return moment.utc(this.inputMetadata.format.duration * 1000).format('HH:mm:ss');
      }
      return '';
    },
    inputResolution() {
      const resolutions = [];
      if (this.inputMetadata && this.inputMetadata.streams) {
        for (let i = 0; i < this.inputMetadata.streams.length; i++) {
          if (this.inputMetadata.streams[i].codec_type === 'video') {
            resolutions.push(`${this.inputMetadata.streams[i].width}x${this.inputMetadata.streams[i].height}`);
          }
        }
      }
      return resolutions;
    },
    jobs() {
      return this.$store.getters['jobs/getFileJobs'];
    },
    nextJob() {
      for (let i = 0; i < this.jobs.length; i++) {
        if (this.jobs[i].id === this.job.id && (i + 1) < this.jobs.length) {
          return this.jobs[i + 1].id;
        }
      }
      return 0;
    },
    dict() {
      return this.$store.getters.dictionary;
    },
  },
  methods: {
    pollData(jobId) {
      this.polling = setInterval(() => {
        this.getJobById(jobId, false);
      }, 10000);
    },
    updateTimer() {
      setInterval(() => {
        if (this.job.state !== 'done') {
          const now = Date.now();
          const played = now - moment(this.job.create_time).utc();
          this.jobTimeCalc = moment.utc(played).format('HH:mm:ss');
        }
      }, 100);
    },
    getJobById(jobId, notification = false) {
      if (notification) {
        this.$store.commit('setPageLoader');
      }
      this.events = [];
      this.displayLogs = false;
      this.$store.dispatch('jobs/getJobById', jobId)
        .then((job) => {
          const outputMap = {};
          this.job = job;
          for (let i = 0; i < job.outputs.length; i += 1) {
            this.outputsMetadata[i] = {};
            this.job.outputs[i].drmInfo = false;
            this.job.outputs[i].showSubtitles = false;
            outputMap[this.job.outputs[i].id] = i + 1;
          }
          this.$store.dispatch('jobs/getInputMetadata', job.id)
            .then((input) => {
              this.inputMetadata = input;
            });

          this.$store.dispatch('jobs/getOutputMetadata', job)
            .then((metadata) => {
              if (metadata) {
                for (let i = 0; i < metadata.length; i++) {
                  this.outputsMetadata.splice(i, 1, metadata[i].data);
                }
              }
            })
            .finally(() => this.$store.commit('resolvePageLoader'));

          const data = {
            jobId: this.job.id,
            limit: 200,
          };
          this.$store.dispatch('jobs/getLogs', data)
            .then((events) => {
              for (let i = 0; i < events.length; i++) {
                const event = events[i];
                if (event.data.output) {
                  event.data.output.index = outputMap[event.data.output.id];
                }
                this.events.push(event);
              }
            })
            .finally(() => this.logsLoading = false);
        })
        .catch((errorMsg) => {
          if (notification) {
            this.$toasted.error(errorMsg, {
              theme: 'toasted-primary',
              position: 'top-center',
              duration: 10000,
            });
          }
          this.$router.push({ name: 'FileTranscodingDashboard' });
        });
    },
    outputStatus(output) {
      if (output.state === 'pending') {
        return 'pending';
      }
      if (output.state === 'processing') {
        return 'processing';
      }
      if (output.state === 'error') {
        return 'failed';
      }
      return 'finished';
    },
    formatTime(time) {
      return moment(time).format('MMM D, YYYY HH:mm');
    },
    handleCopy(text) {
      setClipboard(text);
      this.$toasted.info('URL has been copied', {
        theme: 'toasted-primary',
        position: 'top-center',
        duration: 1000,
      });
    },
    outputDuration(i) {
      if (this.outputsMetadata && this.outputsMetadata[i] && this.outputsMetadata[i].length > 0) {
        const { duration } = this.outputsMetadata[i][0].format;
        return moment.utc(duration * 1000).format('HH:mm:ss');
      }
      return '';
    },
    outputFileSize(i) {
      if (this.outputsMetadata && this.outputsMetadata[i] && this.outputsMetadata[i].length > 0) {
        let size = 0;
        for (let j = 0; j < this.outputsMetadata[i].length; j++) {
          size += parseInt(this.outputsMetadata[i][j].format.size, 10);
        }
        return formatBytes(size);
      }
      return '';
    },
    outputResolutions(i) {
      const resolutions = [];
      if (this.outputsMetadata && this.outputsMetadata[i] && this.outputsMetadata[i].length > 0) {
        for (let j = 0; j < this.outputsMetadata[i].length; j++) {
          const formatStreams = this.outputsMetadata[i][j];
          for (let k = 0; k < formatStreams.streams.length; k++) {
            const stream = formatStreams.streams[k];
            if (stream.codec_type === 'video') {
              resolutions.push(`${stream.width}x${stream.height}`);
            }
          }
        }
      }
      return resolutions;
    },
    areThereOutputSubtitles(i) {
      const output = this.job.outputs[i];
      if (!output.map) return false;
      for (const i in output.map) {
        const item = output.map[i];
        if (item.source && item.source.includes('.vtt')) {
          return true;
        }
      }
      return false;
    },
    outputSubtitles(i) {
      const output = this.job.outputs[i];
      const sources = [];
      for (const i in output.map) {
        const item = output.map[i];
        sources.push(item.source);
      }
      return sources;
    },
    toggleDRMInfo(i) {
      const output = this.job.outputs[i];
      output.drmInfo = !output.drmInfo;
      Vue.set(this.job.outputs, i, output);
    },
    toggleSubtitles(i) {
      const output = this.job.outputs[i];
      output.showSubtitles = !output.showSubtitles;
      Vue.set(this.job.outputs, i, output);
    },
    formatLogDate(timestamp) {
      return moment.unix(timestamp).format('MMM D, YYYY HH:mm');
    },
    goToNextJob() {
      this.$router.push({
        name: 'FileTranscodingJobDetailPage',
        params: {
          id: this.nextJob,
        },
      });
    },
    logColor(event) {
      if (['job.transc.failed', 'job.output.failed'].includes(event.name)) {
        return '#C71C22';
      }
      if (['job.transc.finished', 'job.output.finished'].includes(event.name)) {
        return '#008d4b';
      }
      return 'grey';
    },
    outputURL(url) {
      if (url.startsWith('origin')) {
        return url.replace('origin', 'http');
      }
      return url;
    },
    text(code) {
      // eslint-disable-next-line no-prototype-builtins
      if (this.dict.hasOwnProperty(code)) {
        return this.dict[code];
      }
      return code;
    },
  },
  watch: {
    '$route.params.id'(newId, oldId) {
      if (newId !== oldId) {
        this.getJobById(newId);
      }
    },
  },
};
</script>
